import { useState, useEffect } from 'react';

import { Container } from '@mui/material';
import * as FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { requestAll } from '../../../../apis/config';
import { projectApis } from '../../../../apis/projectApis';
import { solrApis } from '../../../../apis/solrApis';
import AlertMessage from '../../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../../components/elements/loading/Loading';
import BillingHistory from '../../../../components/page-sections/billing/billing-history/BillingHistory';
import BillingInfo from '../../../../components/page-sections/billing/billing-info/BillingInfo';
import PaymentMethod from '../../../../components/page-sections/billing/payment-method/PaymentMethod';
import { MIME_TYPE } from '../../../../components/page-sections/file-manager/constants';
import Breadcrumbs from '../../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../../components/sections/title-panel/TitlePanel';
import { PAYMENT_TYPES } from '../../../../constants';
import { useProject } from '../../../../contexts/ProjectContext';
import { handleErrors } from '../../../../helpers';
import useToast, { TOAST_TYPE } from '../../../../hooks/useToast';
import { sentenceCase } from '../../../../utils/utils';

const BillingPage = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const { project, refreshProject } = useProject();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [billHistory, setBillHistory] = useState([]);
  const [canDeletePayment, setCanDeletePayment] = useState(false);
  const [payment, setPayment] = useState(null);

  const fetchPayment = async () => {
    const [{ data: cardsRes }, { data: billingInfoRes }] = await requestAll([
      projectApis.getProjectCards(projectId),
      projectApis.getProjectBillingInfo(projectId),
    ]);

    const paymentDetails = {
      cards: cardsRes?.[0],
      billingInfo: billingInfoRes.content?.[0],
    };
    setPayment(paymentDetails);
  };

  const fetchData = async () => {
    try {
      const [{ data: instancesRes }, { data: billHistoryRes }] =
        await requestAll([
          solrApis.getInstances(projectId),
          projectApis.getProjectBillingHistory(projectId),
        ]);

      instancesRes.content.length === 0 && setCanDeletePayment(true);
      setBillHistory(billHistoryRes);

      await fetchPayment();
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleBillingSubmit = async (data) => {
    const billingPayload = {
      ...data,
      paymentType: payment.billingInfo?.paymentType || PAYMENT_TYPES.CARD,
    };

    try {
      payment.billingInfo
        ? await projectApis.updateProjectBillingInfo(
            projectId,
            payment.billingInfo?.id,
            billingPayload
          )
        : await projectApis.createProjectBillingInfo(projectId, billingPayload);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('billingInfo', { ns: 'sectionTitles' }),
          })
        )
      );

      fetchPayment();
    } catch (error) {
      const message = t('submit', {
        ns: 'errors',
        content: t('billing', { ns: 'titles' }),
      });
      showToast(TOAST_TYPE.ERROR, sentenceCase(message));
    }
  };

  const handleAddPayment = async (data) => {
    try {
      const requestPayload = { stripeCardTokenId: data.id };
      await projectApis.createProjectCard(projectId, requestPayload);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('addSuccess', {
            ns: 'notifications',
            text: t('billing.creditCard', { ns: 'project' }),
          })
        )
      );
      fetchPayment();
    } catch (error) {
      const message = t('submit', {
        ns: 'errors',
        content: t('billing.creditCard', { ns: 'project' }),
      });
      showToast(TOAST_TYPE.ERROR, sentenceCase(message));
    }
  };

  const handleDeletePayment = async () => {
    try {
      await projectApis.deleteProjectCard(projectId, project.cards.id);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('billing.creditCard', { ns: 'project' }),
          })
        )
      );
      fetchPayment();
    } catch (error) {
      showToast(TOAST_TYPE.ERROR, t('delete', { ns: 'errors' }));
    }
  };

  const handleUpdatePayment = async (data) => {
    try {
      await projectApis.updateProjectCard(projectId, payment.cards.id, data);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('billing.creditCard', { ns: 'project' }),
          })
        )
      );
      refreshProject();
    } catch (error) {
      const message = t('submit', {
        ns: 'errors',
        content: t('billing.creditCard', { ns: 'project' }),
      });
      showToast(TOAST_TYPE.ERROR, sentenceCase(message));
    }
  };

  const handleHistoryPageChange = async (page) => {
    try {
      const billHistoryRes = await projectApis.getProjectBillingHistory(
        projectId,
        page
      );
      setBillHistory(billHistoryRes.data);
    } catch (error) {
      const message = t('fetch', {
        ns: 'errors',
        content: t('billing', { ns: 'titles' }),
      });
      showToast(TOAST_TYPE.ERROR, sentenceCase(message));
    }
  };

  const downloadReceipt = async (bpsId, fileName) => {
    try {
      const { data } = await projectApis.downloadReceipt(projectId, bpsId);
      const blob = new Blob([data], { type: MIME_TYPE.pdf });
      FileSaver.saveAs(blob, `${fileName}.pdf`);

      const message = t('fileDownloadSuccess', { ns: 'notifications' });
      showToast(TOAST_TYPE.SUCCESS, sentenceCase(message));
    } catch (error) {
      const message = t('fileDownloadError', { ns: 'notifications' });
      showToast(TOAST_TYPE.ERROR, sentenceCase(message));
    }
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={t('fetch', {
          ns: 'errors',
          content: t('billing.billingInfo', { ns: 'project' }),
        })}
      />
    );

  return (
    <Container>
      {project && (
        <>
          <Breadcrumbs />
          <TitlePanel title={t('billing', { ns: 'titles' })} />
          <PaymentMethod
            billingInfo={payment.billingInfo}
            card={payment.cards}
            handleUpdate={handleUpdatePayment}
            handleAdd={handleAddPayment}
            handleDelete={handleDeletePayment}
            canDelete={canDeletePayment}
          />
          <BillingInfo
            billingInfo={payment.billingInfo}
            handleConfirm={handleBillingSubmit}
          />
          <BillingHistory
            billingData={billHistory}
            handlePagination={handleHistoryPageChange}
            downloadReceipt={downloadReceipt}
          />
        </>
      )}
    </Container>
  );
};

export default BillingPage;
